import { useMemo, useState, useCallback } from "react";

/**
 * Hook generico de ordenacao para tabelas legadas custom (que NAO usam
 * PainelTabela). Mantem { key, dir } e devolve linhas ordenadas + toggle.
 *
 * Detecta o tipo do valor automaticamente:
 *   - Date            -> ordena cronologico
 *   - number          -> ordena numerico
 *   - string/outros   -> localeCompare pt-BR com numeric (lida com "Lote 10" > "Lote 9")
 *
 * Valores null/undefined sempre vao para o fim, independente da direcao
 * (padrao de planilha — vazios por ultimo).
 *
 * Uso:
 *   const getValue = (row, key) => CAMPO_POR_KEY[key]?.(row);
 *   const { linhas, sort, toggle } = useOrdenacaoTabela(rows, getValue);
 *   ...
 *   <SortableHeader columnKey="cliente" sort={sort} onSort={toggle}>Cliente</SortableHeader>
 *
 * Default: sem ordenacao ativa (key=null) — mantem a ordem em que `rows`
 * chegou (geralmente ja pre-ordenada pela pagina). O usuario clica para
 * sobrescrever.
 *
 * @param {Array} rows
 * @param {(row, key) => any} getValue
 * @param {{key?: string, dir?: 'asc'|'desc'}} [inicial]
 */
export function useOrdenacaoTabela(rows, getValue, inicial = {}) {
  const [sort, setSort] = useState({
    key: inicial.key || null,
    dir: inicial.dir || "asc",
  });

  const toggle = useCallback((key) => {
    setSort((atual) => {
      if (atual.key !== key) return { key, dir: "asc" };
      // mesma coluna: asc -> desc -> sem ordenacao (volta ao default)
      if (atual.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: "asc" };
    });
  }, []);

  const linhas = useMemo(() => {
    if (!sort.key) return rows;
    const copia = [...rows];
    const fator = sort.dir === "asc" ? 1 : -1;
    copia.sort((a, b) => {
      const va = getValue(a, sort.key);
      const vb = getValue(b, sort.key);
      // nulos/vazios sempre por ultimo
      const aVazio = va == null || va === "";
      const bVazio = vb == null || vb === "";
      if (aVazio && bVazio) return 0;
      if (aVazio) return 1;
      if (bVazio) return -1;
      let cmp;
      if (va instanceof Date && vb instanceof Date) {
        cmp = va.getTime() - vb.getTime();
      } else if (typeof va === "number" && typeof vb === "number") {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), "pt-BR", {
          numeric: true,
          sensitivity: "base",
        });
      }
      return cmp * fator;
    });
    return copia;
  }, [rows, sort, getValue]);

  return { linhas, sort, toggle };
}
